import { useEffect, useState } from 'react';

/**
 * Custom hook that adds a minimum display time for loading states to prevent UI flashing.
 *
 * This improves UX by ensuring loading indicators (like skeletons) are visible for a minimum
 * duration, preventing jarring flashes when APIs respond very quickly.
 *
 * @param isLoading - The actual loading state from your API/data fetching
 * @param delay - Minimum time (in ms) to show loading state after it becomes false (default: 300ms)
 * @returns Boolean indicating whether loading UI should be displayed
 *
 * @example
 * ```tsx
 * const shouldShowSkeleton = useDelayedLoading(isLoadingData);
 * return shouldShowSkeleton ? <Skeleton /> : <ActualContent />;
 * ```
 */
export const useDelayedLoading = (isLoading: boolean, delay = 300) => {
  const [showLoading, setShowLoading] = useState(isLoading);

  useEffect(() => {
    if (isLoading) {
      setShowLoading(true);
    } else {
      const timeoutId = setTimeout(() => {
        setShowLoading(false);
      }, delay);

      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, delay]);

  return showLoading;
};
